import type { FirebaseApp } from 'firebase/app';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';

import { compressImage, compressImageThumbnail } from '../utils/imageUtils';
import { generateStorageKey } from '../utils/ids';

export interface AthletePhotoUploadResult {
  imageUrl: string;
  imageThumbnailUrl?: string;
  /** Storage object paths (for `deleteAthleteProfilePhotos`). */
  storagePathFull: string;
  storagePathThumb?: string;
}

/** Payload for `PhotoUpload` `onImageUpload` (URLs + paths; empty strings = cleared). */
export type AthletePhotoPayload = {
  imageUrl: string;
  imageThumbnailUrl: string;
  storagePathFull: string;
  storagePathThumb: string;
};

async function uploadCompressedJpeg(
  app: FirebaseApp,
  objectPath: string,
  blob: Blob,
): Promise<{ url: string; fullPath: string }> {
  const storage = getStorage(app);
  const objectRef = ref(storage, objectPath);
  await uploadBytes(objectRef, blob, { contentType: 'image/jpeg' });
  const url = await getDownloadURL(objectRef);
  return { url, fullPath: objectRef.fullPath };
}

function swallowStorageObjectNotFound(err: unknown): void {
  const code =
    err && typeof err === 'object' && 'code' in err
      ? String((err as { code: unknown }).code)
      : '';
  if (code === 'storage/object-not-found') {
    return;
  }
  throw err;
}

/**
 * Compress, upload full + thumbnail to Storage under the coach’s tryout folder.
 * Paths must match `storage.rules` (`tryouts/{tryoutId}/athletePhotos/{coachUid}/…`).
 */
export async function uploadAthleteProfilePhotos(
  app: FirebaseApp,
  tryoutId: string,
  coachUid: string,
  file: File,
): Promise<AthletePhotoUploadResult> {
  if (!tryoutId || !coachUid || !file) {
    throw new Error('Tryout, coach, and file are required');
  }

  const key = generateStorageKey();
  const base = `tryouts/${tryoutId}/athletePhotos/${coachUid}/${key}`;

  const [compressedImage, compressedThumbnail] = await Promise.all([
    compressImage(file),
    compressImageThumbnail(file),
  ]);

  const [full, thumb] = await Promise.all([
    uploadCompressedJpeg(app, `${base}_full.jpg`, compressedImage),
    uploadCompressedJpeg(app, `${base}_thumb.jpg`, compressedThumbnail),
  ]);

  return {
    imageUrl: full.url,
    imageThumbnailUrl: thumb.url,
    storagePathFull: full.fullPath,
    storagePathThumb: thumb.fullPath,
  };
}

/**
 * Compress + upload a single scoring photo (no thumbnail).
 */
export async function uploadAthleteScorePhoto(
  app: FirebaseApp,
  tryoutId: string,
  coachUid: string,
  file: File,
): Promise<AthletePhotoUploadResult> {
  if (!tryoutId || !coachUid || !file) {
    throw new Error('Tryout, coach, and file are required');
  }
  const key = generateStorageKey();
  const objectPath = `tryouts/${tryoutId}/athletePhotos/${coachUid}/${key}_score.jpg`;
  const compressed = await compressImage(file);
  const full = await uploadCompressedJpeg(app, objectPath, compressed);
  return {
    imageUrl: full.url,
    storagePathFull: full.fullPath,
  };
}

/**
 * Deletes full + thumbnail objects created by `uploadAthleteProfilePhotos`.
 * Ignores “object not found” so remove is idempotent.
 */
export async function deleteAthleteProfilePhotos(
  app: FirebaseApp,
  storagePathFull: string,
  storagePathThumb: string,
): Promise<void> {
  const full = storagePathFull.trim();
  const thumb = storagePathThumb.trim();
  if (!full && !thumb) {
    return;
  }
  const storage = getStorage(app);
  const deletes: Promise<void>[] = [];
  if (full) {
    deletes.push(deleteObject(ref(storage, full)).catch(swallowStorageObjectNotFound));
  }
  if (thumb) {
    deletes.push(deleteObject(ref(storage, thumb)).catch(swallowStorageObjectNotFound));
  }
  await Promise.all(deletes);
}
